/**
 * The spell book and the faith panel, as the client reads them.
 *
 * The world sends the content itself, as the tools wrote it. These are its
 * shapes, for the client, which does not read content files. The server checks
 * in a test that what the content package calls a spell fits what is written
 * here, so the two cannot drift apart without somebody hearing about it.
 */

export interface IconWire {
  glyph: string;
  colour: string;
  ground?: string;
}

export interface ModifierWire {
  stat: string;
  percent: number;
}

export interface StatusWire {
  name: string;
  ticks: number;
  modifiers: ModifierWire[];
  stun: boolean;
  bind: boolean;
  poison: number;
  every: number;
  helpful: boolean;
  chance: string;
}

export type SpellShapeWire = 'square' | 'plus' | 'diamond' | 'circle' | 'line';

export interface ProjectileWire {
  look: string;
  model: string;
  colour?: string;
  colour2?: string;
  speed: number;
  size: number;
  arc: number;
}

export type SpellTargetWire = 'self' | 'enemy' | 'friend' | 'tile' | 'item';

export interface SpellWire {
  id: number;
  name: string;
  description: string;
  book: string;
  order: number;
  icon: IconWire;
  level: number;
  experience: number;
  mana: number;
  components: { item: number; count: number }[];
  /** In the condition language acts use; the tools edit these as they edit any. */
  requires: Record<string, unknown>[];
  target: SpellTargetWire;
  range: number;
  radius: number;
  shape: SpellShapeWire;
  autocast: boolean;
  /** Which rung of the elemental ladder it is on, or empty for a spell on its own. */
  tier: string;
  /** Cast without waiting for the attack queue. Off for nearly everything. */
  instant: boolean;
  speed: number;
  projectile: ProjectileWire;
  accuracy: string;
  sounds: { cast: string; impact: string };
  damage?: { max: number; perLevels: number; element: string };
  heals: number;
  restores: { of: 'mana' | 'run' | 'faith'; amount: number }[];
  status?: StatusWire;
  item?: { consumes: boolean; coinsPercent: number };
  /** In the effect language acts use. */
  effects: Record<string, unknown>[];
}

export interface PrayerWire {
  id: number;
  name: string;
  description: string;
  category: string;
  order: number;
  icon: IconWire;
  level: number;
  group: string;
  drain: number;
  requires: Record<string, unknown>[];
  modifiers: ModifierWire[];
}

export interface SpellbookWire {
  spells: SpellWire[];
  prayers: PrayerWire[];
}

/** What a modifier is called where somebody reads it. */
export const MODIFIER_NAMES: Readonly<Record<string, string>> = {
  meleeAccuracy: 'Melee accuracy',
  meleeDamage: 'Melee damage',
  rangedAccuracy: 'Ranged accuracy',
  rangedDamage: 'Ranged damage',
  magicAccuracy: 'Magic accuracy',
  magicDamage: 'Magic damage',
  defence: 'Defence',
  protectMelee: 'Protection from melee',
  protectRanged: 'Protection from missiles',
  protectMagic: 'Protection from magic',
  healthRegen: 'Health recovery',
  manaRegen: 'Mana recovery',
  faithDrain: 'Faith drain',
};

/** One page of the collection log: a creature, a boss, or a skill, and what it gives. */
export interface CollectionPageWire {
  title: string;
  group: 'Bosses' | 'Creatures' | 'Gathering' | 'Making';
  items: number[];
}

export interface CollectionWire {
  pages: CollectionPageWire[];
  /** How many of each has been come by, by item id. */
  have: Record<string, number>;
}

/** A sound in the library, as the client plays it and the tools list it. */
export interface SoundWire {
  name: string;
  file: string;
  title: string;
  volume: number;
  vary: number;
}

/** Which sounds a moment plays. */
export interface SoundCueWire {
  cue: string;
  sounds: string[];
  volume: number;
}

export interface SoundLibraryWire {
  sounds: SoundWire[];
  cues: SoundCueWire[];
  /** Characters with sounds of their own, by id. */
  npcs: { id: number; attack: string; hurt: string; die: string }[];
}
